import { CLSMetricWithAttribution } from "web-vitals";
import { debugLog } from "./utils/logger";
import { ConnectionInfo, getConnectionInfo } from "./connection";
import { collectElementData } from "./collectElementData";
import { LSData } from "./types";

type TransformedCLSEntry = {
    value: number;
    startTime: number;
    hadRecentInput: boolean;
    lastInputTime?: number;
    sources: {
        currentRect: DOMRectReadOnly;
        previousRect: DOMRectReadOnly;
        element: any; // Detailed element data or null
        largest?: boolean; // Mark if this is the largest shift source
    }[];
    largest?: boolean; // Mark if this is the largest shift entry
};

type TransformedCLSMetric = {
    siteId: string;
    sessionId: string;
    connection: ConnectionInfo;
    version: string;
    delta: number;
    value: number;
    id: string;
    viewport?: { width: number; height: number };
    entries: TransformedCLSEntry[];
    attribution: {
        largestShiftTarget: string | null;
        loadState?: CLSMetricWithAttribution['attribution']['loadState'];
    };
    navigationType?: CLSMetricWithAttribution['navigationType'];
};

const markLargestShift = (data: TransformedCLSMetric, metric: CLSMetricWithAttribution) => {
    const largestEntry = data.entries.find(entry =>
        entry.value === metric.attribution.largestShiftValue
        && entry.startTime === metric.attribution.largestShiftTime);

    if (largestEntry) {
        largestEntry.largest = true;
    }

    const largestSource = largestEntry?.sources.find(source => {
        return source.currentRect.x === metric.attribution.largestShiftSource?.currentRect.x
            && source.currentRect.y === metric.attribution.largestShiftSource?.currentRect.y
            && source.currentRect.width === metric.attribution.largestShiftSource?.currentRect.width
            && source.currentRect.height === metric.attribution.largestShiftSource?.currentRect.height
            && source.currentRect.top === metric.attribution.largestShiftSource?.currentRect.top
            && source.currentRect.right === metric.attribution.largestShiftSource?.currentRect.right
            && source.currentRect.bottom === metric.attribution.largestShiftSource?.currentRect.bottom
            && source.currentRect.left === metric.attribution.largestShiftSource?.currentRect.left
            && source.previousRect.x === metric.attribution.largestShiftSource?.previousRect.x
            && source.previousRect.y === metric.attribution.largestShiftSource?.previousRect.y
            && source.previousRect.width === metric.attribution.largestShiftSource?.previousRect.width
            && source.previousRect.height === metric.attribution.largestShiftSource?.previousRect.height
            && source.previousRect.top === metric.attribution.largestShiftSource?.previousRect.top
            && source.previousRect.right === metric.attribution.largestShiftSource?.previousRect.right
            && source.previousRect.bottom === metric.attribution.largestShiftSource?.previousRect.bottom
            && source.previousRect.left === metric.attribution.largestShiftSource?.previousRect.left;
    });

    if (largestSource) {
        largestSource.largest = true;
    }
}

export const transformCLSMetric = (metric: CLSMetricWithAttribution, siteId: string, sessionId: string, version: string): LSData => {
    debugLog("received CLS:", metric);

    const data: TransformedCLSMetric = {
        siteId,
        sessionId,
        connection: getConnectionInfo(),
        version,
        delta: metric.delta,
        value: metric.value,
        id: metric.id,
        // Capture viewport once at metric level (not per-source)
        viewport: {
            width: typeof window !== 'undefined' ? window.innerWidth : 0,
            height: typeof window !== 'undefined' ? window.innerHeight : 0,
        } as any,
        navigationType: metric.navigationType,
        entries: metric.entries.map(entry => ({
            // duration is not needed as it always is 0 for CLS https://developer.mozilla.org/en-US/docs/Web/API/LayoutShift
            value: entry.value,
            startTime: entry.startTime,
            hadRecentInput: entry.hadRecentInput,
            lastInputTime: (entry as any).lastInputTime,
            sources: entry.sources.map(source => {
                let elementData = null;

                // Collect detailed data for all visible shifts to improve visualization
                if (source.node) {
                    // Pass precomputed currentRect to avoid extra layout read
                    elementData = collectElementData(source.node as Element, source.currentRect as any);
                }

                return {
                    currentRect: source.currentRect,
                    previousRect: source.previousRect,
                    element: elementData
                };
            }) || []
        })),
        attribution: {
            largestShiftTarget: metric.attribution.largestShiftTarget || null,
            loadState: metric.attribution.loadState,
        }
    };

    // Mark the largest shift entry and source
    markLargestShift(data, metric);

    debugLog("transformed CLS", data)

    return data;
}

